/**
 * Открытие приложения отпечатком или лицом.
 *
 * Четыре цифры утром — это терпимо, но приложение, которое открывается
 * прикосновением, человек не выключит через неделю. А выключенная защита не
 * защищает вовсе, так что удобство здесь — часть защиты, а не украшение.
 *
 * Пароль при этом остаётся обязательным: биометрия не срабатывает с мокрыми
 * руками, в перчатках и в маске, а дверь без запасного ключа однажды закроется
 * навсегда.
 *
 * Отдельный интерфейс, а не прямые вызовы плагина: браузер биометрии не знает
 * вовсе, и заглушка честнее ошибки в консоли.
 */

import type { BiometricAuthPlugin } from '@aparajita/capacitor-biometric-auth';
import { Capacitor } from '@capacitor/core';

export interface BiometricsPort {
  /** Устройство умеет и биометрия настроена. */
  available(): Promise<boolean>;
  /** `true` — человек подтвердил себя. Отказ и сбой неразличимы намеренно. */
  authenticate(): Promise<boolean>;
}

export const unavailableBiometrics: BiometricsPort = {
  available: () => Promise.resolve(false),
  authenticate: () => Promise.resolve(false),
};

export const nativeBiometrics: BiometricsPort = {
  async available() {
    const plugin = (await load())?.plugin;
    if (!plugin) return false;

    try {
      const { isAvailable } = await plugin.checkBiometry();
      return isAvailable;
    } catch {
      return false;
    }
  },

  async authenticate() {
    const plugin = (await load())?.plugin;
    if (!plugin) return false;

    try {
      await plugin.authenticate({
        reason: 'Открыть Kairo',
        cancelTitle: 'Ввести пароль',
        allowDeviceCredential: false,
      });
      return true;
    } catch {
      /*
        Плагин бросает и при отказе человека, и при сбое датчика. Различать их
        здесь незачем: в обоих случаях остаётся ввести пароль, и разные сообщения
        только запутали бы.
      */
      return false;
    }
  },
};

/**
 * Плагин в обёртке.
 *
 * Его интерфейс отвечает на любое обращение, включая `then`. Возвращённый из
 * асинхронной функции как есть, он принимается за «обещание», движок зовёт
 * `BiometricAuthNative.then()`, и вызов падает — вход по отпечатку молча не
 * работал по этой самой причине.
 */
async function load(): Promise<{ plugin: BiometricAuthPlugin } | null> {
  if (!Capacitor.isNativePlatform()) return null;

  const { BiometricAuth } = await import('@aparajita/capacitor-biometric-auth');
  return { plugin: BiometricAuth };
}

export function systemBiometrics(): BiometricsPort {
  return Capacitor.isNativePlatform() ? nativeBiometrics : unavailableBiometrics;
}
